// -----------------------------------------------------------------------------
// The API envelope.
//
// Mirrors `Backend/src/utils/response.ts` and `Backend/src/utils/errors.ts`
// exactly. When either changes, this file changes with it - these are the two
// shapes every screen in the panel is built on.
// -----------------------------------------------------------------------------

export type SuccessBody<T> = {
  success: true;
  data: T;
  meta?: Record<string, unknown>;
};

export type ErrorBody = {
  success: false;
  error: {
    code: string;
    message: string;
    details: unknown;
    requestId: string;
  };
};

/** Cursor pagination - the only pagination style this API has. */
export type PageMeta = {
  hasMore: boolean;
  /** Opaque. Pass back verbatim as `?cursor=`; never parse or construct one. */
  nextCursor: string | null;
};

export type Page<T> = {
  items: T[];
  page: PageMeta;
};

/**
 * The stable error codes, copied from `Backend/src/utils/errors.ts`.
 *
 * Switch on these. NEVER on `message` - messages are copy, they get reworded,
 * and a UI that branches on prose breaks the first time someone fixes a typo.
 */
export const ErrorCode = {
  AUTH_REQUIRED: 'AUTH_REQUIRED',
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  AUTH_INVALID: 'AUTH_INVALID',
  APP_CHECK_FAILED: 'APP_CHECK_FAILED',
  FORBIDDEN: 'FORBIDDEN',
  ROLE_MISMATCH: 'ROLE_MISMATCH',
  ACCOUNT_SUSPENDED: 'ACCOUNT_SUSPENDED',
  ONBOARDING_INCOMPLETE: 'ONBOARDING_INCOMPLETE',
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',
  RESOURCE_CONFLICT: 'RESOURCE_CONFLICT',
  RATE_LIMITED: 'RATE_LIMITED',
  PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
  UNSUPPORTED_MEDIA_TYPE: 'UNSUPPORTED_MEDIA_TYPE',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
  EXTERNAL_SERVICE_ERROR: 'EXTERNAL_SERVICE_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR',

  // Client-side only. The server never sends these; the API client mints them so
  // that a transport failure is still an ApiError with a code to switch on,
  // rather than a bare TypeError from fetch.
  NETWORK_ERROR: 'NETWORK_ERROR',
  TIMEOUT: 'TIMEOUT',
  CANCELLED: 'CANCELLED',
  MALFORMED_RESPONSE: 'MALFORMED_RESPONSE',
} as const;

export type ErrorCodeValue = (typeof ErrorCode)[keyof typeof ErrorCode];

/**
 * `VALIDATION_FAILED` carries `details` as a field-path → message map, which is
 * what binds a server rejection onto the right form control.
 */
export type ValidationDetails = Record<string, string>;
